import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://traindriverfoundation.com";
const DEFAULT_IMAGE = `${SITE_URL}/platform-sunset-manchester.jpg`;

function buildCanonical(path = "/") {
  if (!path || path === "/") {
    return `${SITE_URL}/`;
  }

  const cleanPath = String(path)
    .split("?")[0]
    .split("#")[0]
    .replace(/^https?:\/\/(www\.)?traindriverfoundation\.com/i, "")
    .replace(/^\/+/, "/");

  if (!cleanPath || cleanPath === "/") {
    return `${SITE_URL}/`;
  }

  const normalisedPath = `/${cleanPath}`
    .replace(/\/+/g, "/")
    .replace(/\/$/, "");

  return `${SITE_URL}${normalisedPath}`;
}

/**
 * Reusable per-page SEO meta component.
 * - title: page title (will be suffixed with " | Train Driver Foundation")
 * - description: 150–160 character meta description
 * - path: the pathname, used for canonical + og:url
 * - image: absolute URL to an OG image (optional; falls back to default)
 * - jsonLd: optional array of JSON-LD schema objects for GEO/LLM discoverability
 * - noindex: set true on hidden post-checkout pages
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  jsonLd = null,
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} | Train Driver Foundation`
    : "Train Driver Foundation | UK Train Driver Recruitment & CPD Training";

  const url = buildCanonical(path);

  const desc =
    description ||
    "Train Driver Foundation (TDF) helps applicants across the UK prepare for the highly competitive trainee train driver selection process with CPD-accredited courses, CV reviews, interview preparation and psychometric practice.";

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />

      {/* Canonical should only be generated here. Do not add another canonical tag elsewhere. */}
      <link rel="canonical" href={url} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Train Driver Foundation" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Helper: build an FAQPage JSON-LD schema from [{q, a}] list.
 * Paste the result into Seo's jsonLd prop.
 */
export function buildFAQSchema(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/**
 * Helper: build a BreadcrumbList JSON-LD schema.
 * items: [{name, path}]
 */
export function buildBreadcrumbSchema(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: buildCanonical(it.path),
    })),
  };
}

/**
 * Helper: build a Course JSON-LD schema for CPD course pages.
 */
export function buildCourseSchema({ name, description, level, hours, price, slug }) {
  const courseUrl = buildCanonical(`/cpd/${slug}`);

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name,
    description,
    provider: {
      "@type": "EducationalOrganization",
      name: "Train Driver Foundation",
      sameAs: SITE_URL,
    },
    url: courseUrl,
    educationalLevel: level,
    timeRequired: hours,
    offers: price
      ? {
          "@type": "Offer",
          price: String(price).replace(/[^0-9.]/g, ""),
          priceCurrency: "GBP",
          availability: "https://schema.org/InStock",
          url: courseUrl,
        }
      : undefined,
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "Online",
      courseWorkload: hours,
    },
  };
}